// src/components/dashboard/PathCharacters.jsx
//
// Artwork for the Guided Path traveller (ids in data/pathCharacters.js).
//
// Every character shares one contract so the trail animation in GuidedPath
// never has to know which it is drawing:
//   - viewBox 0 0 96 44, drawn FACING RIGHT (GuidedPath mirrors it with
//     scaleX(-1) when the trail heads left)
//   - ground line at about y = 40, figure roughly centred on x = 48
//   - clothing uses var(--brand-primary) / var(--brand-accent), so the figure
//     wears the org's colours with no per-client artwork

import React from 'react';

// A musher in a brand-coloured parka on a dog sled.
export const DogSled = () => (
    <svg viewBox="0 0 96 44" className="w-full h-auto drop-shadow-sm" aria-hidden="true">
        {/* tow line */}
        <path d="M 42 31 L 62 29 L 82 28" stroke="#94a3b8" strokeWidth="1.5" fill="none" strokeLinecap="round" />

        {/* wheel dog */}
        <g>
            <path d="M 56 34 v 5 M 60 34 v 5 M 65 34 v 5 M 68 33 v 5" stroke="#64748b" strokeWidth="1.8" strokeLinecap="round" />
            <ellipse cx="62" cy="31" rx="8" ry="4" fill="#64748b" />
            <circle cx="70" cy="28.5" r="3.2" fill="#64748b" />
            <path d="M 68 26 l -1 -3 l 2.6 1.4 z M 71 26 l 1 -3 l -2.6 1.4 z" fill="#64748b" />
            <path d="M 54 31 q -4 -3 -4 -6" stroke="#64748b" strokeWidth="1.8" fill="none" strokeLinecap="round" />
        </g>

        {/* lead dog */}
        <g>
            <path d="M 77 33 v 5 M 81 33 v 5 M 86 33 v 5 M 89 32 v 5" stroke="#475569" strokeWidth="1.8" strokeLinecap="round" />
            <ellipse cx="83" cy="30" rx="8" ry="4" fill="#475569" />
            <circle cx="91" cy="27.5" r="3.2" fill="#475569" />
            <path d="M 89 25 l -1 -3 l 2.6 1.4 z M 92 25 l 1 -3 l -2.6 1.4 z" fill="#475569" />
            <path d="M 75 30 q -4 -3 -4 -6" stroke="#475569" strokeWidth="1.8" fill="none" strokeLinecap="round" />
        </g>

        {/* sled: runner with curled front, deck, handlebar at the back */}
        <path d="M 9 39 H 38 Q 45 39 47 33" stroke="#334155" strokeWidth="2.2" fill="none" strokeLinecap="round" />
        <rect x="13" y="31" width="24" height="4" rx="1.2" fill="#1e293b" />
        <path d="M 13 31 V 18 Q 13 15 16 15 H 19" stroke="#334155" strokeWidth="2.2" fill="none" strokeLinecap="round" />

        {/* musher */}
        <path d="M 22 31 V 24 M 28 31 V 24" stroke="#1e293b" strokeWidth="3" strokeLinecap="round" />
        <rect x="20" y="12" width="10" height="13" rx="3" style={{ fill: 'var(--brand-primary)' }} />
        <path d="M 21 16 L 16 16" stroke="var(--brand-primary)" strokeWidth="3" strokeLinecap="round" />
        <circle cx="25" cy="8" r="4" fill="#fcd5b5" />
        <path d="M 20.5 8 a 4.5 4.5 0 0 1 9 0 v 1.6 h -9 z" style={{ fill: 'var(--brand-primary)' }} />
    </svg>
);

// A skater mid-stride, leaning into it, stick on the ice and the puck just
// ahead. Jersey and helmet in the org's primary, trim in its accent.
export const HockeyPlayer = () => (
    <svg viewBox="0 0 96 44" className="w-full h-auto drop-shadow-sm" aria-hidden="true">
        {/* speed lines trailing behind */}
        <path d="M 12 22 H 24 M 8 29 H 22 M 14 36 H 26" stroke="#94a3b8" strokeWidth="1.5" fill="none" strokeLinecap="round" opacity="0.7" />

        {/* stick: shaft from the hands down to the ice, then the blade */}
        <path d="M 58 19 L 79 39" stroke="#334155" strokeWidth="2" fill="none" strokeLinecap="round" />
        <path d="M 79 39 L 86 39.6" stroke="#1e293b" strokeWidth="2.8" fill="none" strokeLinecap="round" />
        {/* puck */}
        <rect x="88" y="38.2" width="5.5" height="2.4" rx="1" fill="#0f172a" />

        {/* back leg, extended in the push */}
        <path d="M 44 27 L 35 37" stroke="#1e293b" strokeWidth="3.6" fill="none" strokeLinecap="round" />
        {/* front leg, knee bent */}
        <path d="M 47 27 L 55 31 L 55 37" stroke="#1e293b" strokeWidth="3.6" fill="none" strokeLinecap="round" strokeLinejoin="round" />

        {/* skates: boot + blade */}
        <path d="M 31.5 37.5 H 38" stroke="#0f172a" strokeWidth="3" strokeLinecap="round" />
        <path d="M 30 40.3 H 40" stroke="#64748b" strokeWidth="1.4" strokeLinecap="round" />
        <path d="M 53 37.5 H 60" stroke="#0f172a" strokeWidth="3" strokeLinecap="round" />
        <path d="M 51.5 40.3 H 62" stroke="#64748b" strokeWidth="1.4" strokeLinecap="round" />

        {/* pants */}
        <path d="M 42 22 L 50 22 L 49 29 L 41 29 Z" fill="#1e293b" stroke="#1e293b" strokeWidth="2" strokeLinejoin="round" />

        {/* jersey: torso leaning forward */}
        <path d="M 42 23 L 50 23 L 58 13 L 49 10.5 Z" strokeWidth="2.4" strokeLinejoin="round" style={{ fill: 'var(--brand-primary)', stroke: 'var(--brand-primary)' }} />
        {/* hem stripe in the accent colour */}
        <path d="M 42.6 21.2 L 50.6 21.2" strokeWidth="1.8" strokeLinecap="round" style={{ stroke: 'var(--brand-accent)' }} />

        {/* arms: both hands on the stick */}
        <path d="M 54 14 L 61 17 L 58 19" strokeWidth="3.2" fill="none" strokeLinecap="round" strokeLinejoin="round" style={{ stroke: 'var(--brand-primary)' }} />
        <path d="M 51 15 L 56 21 L 62 22.5" strokeWidth="3.2" fill="none" strokeLinecap="round" strokeLinejoin="round" style={{ stroke: 'var(--brand-primary)' }} />
        {/* gloves */}
        <circle cx="58.2" cy="19" r="1.9" fill="#1e293b" />
        <circle cx="62.2" cy="22.6" r="1.9" fill="#1e293b" />

        {/* head + helmet with an accent stripe */}
        <circle cx="58" cy="7.5" r="4" fill="#fcd5b5" />
        <path d="M 53.4 7.6 a 4.6 4.6 0 0 1 9.2 0 v 1.2 h -9.2 z" style={{ fill: 'var(--brand-primary)' }} />
        <path d="M 54 6.2 H 62" strokeWidth="1.2" strokeLinecap="round" style={{ stroke: 'var(--brand-accent)' }} />
    </svg>
);

export const PATH_CHARACTER_ART = { dogsled: DogSled, hockey: HockeyPlayer };
